import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Edit, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { EliteBadge, useActiveMemberIds } from "@/components/Membership";
import { API, formatApiError, getInitials } from "@/lib/shared";
import { EMPTY_PATIENT, PatientChart } from "@/components/PatientChart";

export const PatientManagement = ({ patients, onRefresh, focusPatientId, onFocusConsumed }) => {
  const memberIds = useActiveMemberIds();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PATIENT);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('demographics');
  const [chartPatient, setChartPatient] = useState(null);

  // Auto-open chart when a focus id is passed from another tab (e.g. Calendar → View Chart)
  useEffect(() => {
    if (!focusPatientId) return;
    const p = patients.find(x => x.id === focusPatientId);
    if (p) {
      setChartPatient(p);
      onFocusConsumed && onFocusConsumed();
    }
  }, [focusPatientId, patients, onFocusConsumed]);

  const filteredPatients = patients.filter(p => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return (
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q) ||
      p.phone?.includes(searchTerm) ||
      p.date_of_birth?.includes(searchTerm)
    );
  });

  const openCreate = () => { setEditingPatient(null); setFormData(EMPTY_PATIENT); setActiveSection('demographics'); setModalOpen(true); };
  const openEdit = (p) => {
    setEditingPatient(p);
    setFormData({ ...EMPTY_PATIENT, ...p });
    setActiveSection('demographics');
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      if (payload.hipaa_acknowledged && payload.treatment_consent && !editingPatient?.consent_signed_at) {
        payload.consent_signed_at = new Date().toISOString();
      }
      if (editingPatient) {
        await axios.put(`${API}/patients/${editingPatient.id}`, payload);
        toast.success("Patient updated");
      } else {
        await axios.post(`${API}/patients`, payload);
        toast.success("Patient added");
      }
      setModalOpen(false);
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this patient?")) return;
    try { await axios.delete(`${API}/patients/${id}`); toast.success("Patient deleted"); onRefresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const sections = [
    { id: 'demographics', label: 'Demographics' },
    { id: 'contact', label: 'Emergency Contact' },
    { id: 'medical', label: 'Medical History' },
    { id: 'iv', label: 'IV Therapy' },
    { id: 'consent', label: 'Consent' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-lg font-semibold">Patients</h2>
        <Button size="sm" onClick={openCreate} data-testid="add-patient-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />New Patient Intake</Button>
      </div>
      <Input placeholder="Search by name, DOB, phone, or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-sm" data-testid="patient-search" />
      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {filteredPatients.map(p => (
          <div key={p.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group" onClick={() => setChartPatient(p)} data-testid={`patient-row-${p.id}`}>
            <div className="flex items-center gap-3 flex-1">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary/10 text-primary">{getInitials(p.first_name, p.last_name)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">
                  {p.first_name} {p.last_name}
                  {memberIds.has(p.id) && <EliteBadge className="ml-2" testId={`patient-elite-${p.id}`} />}
                  {p.intake_completed_at && (
                    <Badge variant="outline" className="ml-2 text-[9px] border-emerald-500/50 text-emerald-300 bg-emerald-500/10" data-testid={`patient-intake-done-${p.id}`}>
                      ✓ Intake
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">{p.date_of_birth ? `DOB: ${p.date_of_birth} · ` : ''}{p.phone || p.email || 'No contact'}</p>
                {p.allergies && <p className="text-xs text-destructive mt-0.5">⚠ Allergies: {p.allergies}</p>}
              </div>
            </div>
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setChartPatient(p)} data-testid={`open-chart-${p.id}`}><FileText className="w-4 h-4 mr-1" />Open Chart</Button>
              <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-patient-${p.id}`}><Edit className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(p.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {filteredPatients.length === 0 && <p className="text-center text-muted-foreground py-4">No patients found</p>}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPatient ? "Edit Patient" : "New Patient Intake"}</DialogTitle>
            <DialogDescription>Complete medical record</DialogDescription>
          </DialogHeader>

          {/* Section pills */}
          <div className="flex flex-wrap gap-2 border-b border-border/40 pb-3">
            {sections.map(s => (
              <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeSection === s.id ? 'bg-primary text-primary-foreground' : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}>
                {s.label}
              </button>
            ))}
          </div>

          {activeSection === 'demographics' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>First Name *</Label><Input value={formData.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="patient-firstname" /></div>
                <div className="space-y-1"><Label>Last Name *</Label><Input value={formData.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="patient-lastname" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Date of Birth</Label><Input type="date" value={formData.date_of_birth} onChange={(e) => update('date_of_birth', e.target.value)} /></div>
                <div className="space-y-1"><Label>Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => update('gender', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="nonbinary">Non-binary</SelectItem>
                      <SelectItem value="prefer_not">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => update('phone', e.target.value)} placeholder="555-123-4567" /></div>
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => update('email', e.target.value)} /></div>
              </div>
              <div className="space-y-1"><Label>Address</Label><Input value={formData.address} onChange={(e) => update('address', e.target.value)} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label>City</Label><Input value={formData.city} onChange={(e) => update('city', e.target.value)} /></div>
                <div className="space-y-1"><Label>State</Label><Input value={formData.state} onChange={(e) => update('state', e.target.value)} placeholder="FL" /></div>
                <div className="space-y-1"><Label>ZIP</Label><Input value={formData.zip_code} onChange={(e) => update('zip_code', e.target.value)} /></div>
              </div>
            </div>
          )}

          {activeSection === 'contact' && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Emergency Contact Name</Label><Input value={formData.emergency_contact_name} onChange={(e) => update('emergency_contact_name', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Phone</Label><Input value={formData.emergency_contact_phone} onChange={(e) => update('emergency_contact_phone', e.target.value)} /></div>
                <div className="space-y-1"><Label>Relationship</Label><Input value={formData.emergency_contact_relationship} onChange={(e) => update('emergency_contact_relationship', e.target.value)} placeholder="Spouse, Parent..." /></div>
              </div>
            </div>
          )}

          {activeSection === 'medical' && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-destructive">⚠ Allergies (drug / food / environmental)</Label><Textarea rows={2} value={formData.allergies} onChange={(e) => update('allergies', e.target.value)} placeholder="None, or list all known allergies" /></div>
              <div className="space-y-1"><Label>Current Medications</Label><Textarea rows={2} value={formData.current_medications} onChange={(e) => update('current_medications', e.target.value)} placeholder="Include dose & frequency" /></div>
              <div className="space-y-1"><Label>Medical Conditions</Label><Textarea rows={2} value={formData.medical_conditions} onChange={(e) => update('medical_conditions', e.target.value)} placeholder="Diabetes, hypertension, kidney disease, pregnancy, etc." /></div>
              <div className="space-y-1"><Label>Past Surgeries</Label><Textarea rows={2} value={formData.past_surgeries} onChange={(e) => update('past_surgeries', e.target.value)} /></div>
            </div>
          )}

          {activeSection === 'iv' && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Switch checked={!!formData.prior_iv_therapy} onCheckedChange={(v) => update('prior_iv_therapy', v)} />
                <Label>Patient has had prior IV therapy</Label>
              </div>
              {formData.prior_iv_therapy && (
                <div className="space-y-1"><Label>Prior IV Therapy Notes</Label><Textarea rows={2} value={formData.prior_iv_notes} onChange={(e) => update('prior_iv_notes', e.target.value)} placeholder="Type of therapy, frequency, reactions" /></div>
              )}
              <div className="space-y-1"><Label>Hydration Concerns / Goals</Label><Textarea rows={2} value={formData.hydration_concerns} onChange={(e) => update('hydration_concerns', e.target.value)} placeholder="Fatigue, recovery, immune support..." /></div>
            </div>
          )}

          {activeSection === 'consent' && (
            <div className="space-y-4">
              <div className="bg-muted/30 p-3 rounded-lg border border-border/50">
                <div className="flex items-start gap-3">
                  <Switch checked={!!formData.hipaa_acknowledged} onCheckedChange={(v) => update('hipaa_acknowledged', v)} className="mt-1" />
                  <div>
                    <Label className="font-semibold">HIPAA Privacy Acknowledgment</Label>
                    <p className="text-xs text-muted-foreground mt-1">Patient acknowledges receipt of HIPAA Privacy Notice and understands how their protected health information will be used and disclosed.</p>
                  </div>
                </div>
              </div>
              <div className="bg-muted/30 p-3 rounded-lg border border-border/50">
                <div className="flex items-start gap-3">
                  <Switch checked={!!formData.treatment_consent} onCheckedChange={(v) => update('treatment_consent', v)} className="mt-1" />
                  <div>
                    <Label className="font-semibold">Treatment Consent</Label>
                    <p className="text-xs text-muted-foreground mt-1">Patient consents to IV therapy treatment and acknowledges the discussed risks, benefits and alternatives.</p>
                  </div>
                </div>
              </div>
              {editingPatient?.consent_signed_at && (
                <p className="text-xs text-primary">✓ Consent signed: {new Date(editingPatient.consent_signed_at).toLocaleString()}</p>
              )}
              <div className="space-y-1"><Label>Additional Notes</Label><Textarea rows={2} value={formData.notes} onChange={(e) => update('notes', e.target.value)} /></div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-patient-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold">{editingPatient ? "Update" : "Save Patient"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PatientChart
        patient={chartPatient}
        onClose={() => setChartPatient(null)}
        onEdit={(p) => { setChartPatient(null); openEdit(p); }}
      />
    </div>
  );
};

// ==================== SERVICE MANAGEMENT ====================
